import { FormEvent, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Plus, Search, UserPlus } from 'lucide-react';
import { Link } from 'react-router-dom';
import API from '../../../utils/constant';
import { fieldClass } from '../../lib/fieldClass';
import { useSortableTable, SortColumn } from '../../lib/useSortableTable';
import usePagination from '../../lib/usePagination';
import {
  Badge, Button, ConfirmDialog, EmptyState, ErrorPanel, FormField, LoadingState, Modal, PageHeader,
  PanelCard, Pager, PillTabs, SortableTh, Table, Td, Th,
} from '../../components/ui';
import { RadioCards } from '../../components/ui/Radio';

export type StaffRole = 'teacher' | 'counselor' | 'school_admin';
export type StaffRow = {
  profile_id: number; user_id: number; username: string; first_name: string; last_name: string;
  role: StaffRole; is_active: boolean; must_change_password: boolean; assignment_count: number; last_login: string | null;
};
type Credentials = { name: string; username: string; password: string; fresh: boolean };
type RoleFilter = 'all' | StaffRole;

const ROLE_LABEL: Record<StaffRole, string> = { teacher: 'Teacher', counselor: 'Counselor', school_admin: 'School admin' };
const fullName = (s: StaffRow) => `${s.first_name} ${s.last_name}`.trim() || s.username;
const PAGE_SIZE = 20;

const COLUMNS: SortColumn<StaffRow>[] = [
  { key: 'name', accessor: (s) => `${s.last_name} ${s.first_name}`.trim() || s.username },
  { key: 'role', accessor: (s) => ROLE_LABEL[s.role] },
  { key: 'status', accessor: (s) => (!s.is_active ? 2 : s.must_change_password ? 1 : 0), type: 'number' },
  { key: 'classes', accessor: (s) => (s.role === 'teacher' ? s.assignment_count : null), type: 'number' },
  { key: 'last', accessor: (s) => s.last_login, type: 'date' },
];

const errText = (err: any, fallback: string) => {
  const d = err?.response?.data;
  if (d && typeof d === 'object') {
    const first = Object.values(d).find((v) => typeof v === 'string');
    if (typeof first === 'string') return first;
  }
  return fallback;
};

function lastSeen(iso: string | null) {
  if (!iso) return 'Never';
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function StatusBadge({ s }: { s: StaffRow }) {
  if (!s.is_active) return <Badge tone="neutral">Deactivated</Badge>;
  if (s.must_change_password) return <Badge tone="warning">Waiting for first sign-in</Badge>;
  return <Badge tone="success">Active</Badge>;
}

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); window.setTimeout(() => setCopied(false), 1500); } catch {}
  };
  return (
    <div>
      <p className="mb-1 text-[13px] font-semibold text-ink">{label}</p>
      <div className="flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded-[10px] bg-surface-2 px-3 py-2.5 font-mono text-[15px] text-ink">{value}</code>
        <Button variant="ghost" size="sm" icon={copied ? <Check size={16} aria-hidden /> : <Copy size={16} aria-hidden />} onClick={copy} aria-label={`Copy ${label.toLowerCase()}`}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </div>
  );
}

// Shown once after create / reset: the temporary password is never shown again.
function CredentialsDialog({ creds, onClose }: { creds: Credentials | null; onClose: () => void }) {
  return (
    <Modal isOpen={creds !== null} onClose={onClose} title={creds?.fresh ? `${creds.name} can sign in now` : `New password for ${creds?.name ?? ''}`}
      footer={<Button onClick={onClose}>Done</Button>}>
      {creds && (
        <div className="space-y-4">
          <p className="text-sm text-muted">
            Give these to {creds.name} in person or by a private message. They sign in to the YADA School app or this website
            and choose their own password straight away.
          </p>
          <CopyField label="Username" value={creds.username} />
          <CopyField label="Temporary password" value={creds.password} />
          <p role="note" className="rounded-[12px] bg-warning-bg px-3 py-2 text-[13px] text-warning-ink">
            This password is shown only once. If it's lost, use Reset password to make a new one.
          </p>
        </div>
      )}
    </Modal>
  );
}

function AddStaffDrawer({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (c: Credentials) => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ first_name: '', last_name: '', role: 'teacher' as 'teacher' | 'counselor', username: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const create = useMutation({
    mutationFn: () => API.post('/api/school/staff/', {
      first_name: form.first_name.trim(), last_name: form.last_name.trim(), role: form.role,
      ...(form.username.trim() ? { username: form.username.trim() } : {}),
    }).then((r) => r.data as StaffRow & { temporary_password: string }),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['school-staff'] });
      qc.invalidateQueries({ queryKey: ['school-setup-summary'] });
      setForm({ first_name: '', last_name: '', role: 'teacher', username: '' });
      onCreated({ name: fullName(row), username: row.username, password: row.temporary_password, fresh: true });
    },
    onError: (err: any) => {
      const d = err?.response?.data;
      setErrors(d && typeof d === 'object' && !d.detail ? d : { form: errText(err, 'Could not add this person. Try again.') });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!form.first_name.trim()) errs.first_name = 'Enter a first name.';
    if (!form.last_name.trim()) errs.last_name = 'Enter a last name.';
    setErrors(errs);
    if (!Object.keys(errs).length) create.mutate();
  };
  return (
    <Modal isOpen={open} onClose={onClose} side="right" eyebrow="Staff" title="Add a staff member"
      subtitle="They get a temporary password to sign in with, and choose their own the first time.">
      <form onSubmit={submit} noValidate className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="First name" htmlFor="staff-first" error={errors.first_name}>
            <input id="staff-first" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={fieldClass} autoComplete="off" />
          </FormField>
          <FormField label="Last name" htmlFor="staff-last" error={errors.last_name}>
            <input id="staff-last" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className={fieldClass} autoComplete="off" />
          </FormField>
        </div>
        <div>
          <p className="mb-2 text-[13px] font-semibold text-ink">Role</p>
          <RadioCards label="Role" value={form.role} onChange={(role) => setForm({ ...form, role })} options={[
            { key: 'teacher', label: 'Teacher', hint: 'Sets and marks exams for the grades and subjects you assign.' },
            { key: 'counselor', label: 'Counselor', hint: 'Sees wellbeing alerts and handles student cases.' },
          ]} />
          {errors.role && <p className="mt-1 text-xs text-danger-ink">{errors.role}</p>}
        </div>
        <FormField label="Username" htmlFor="staff-username" optional hint="Leave blank and we'll make one from their name." error={errors.username}>
          <input id="staff-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className={fieldClass} autoComplete="off" />
        </FormField>
        {errors.form && <p role="alert" className="rounded-[12px] bg-danger-bg px-3 py-2 text-sm text-danger-ink">{errors.form}</p>}
        <Button type="submit" className="w-full" loading={create.isPending} icon={<UserPlus size={16} aria-hidden />}>Add and get sign-in details</Button>
      </form>
    </Modal>
  );
}

type Pending = { kind: 'reset' | 'deactivate' | 'reactivate' | 'role'; s: StaffRow } | null;

export default function StaffPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['school-staff'], queryFn: () => API.get<{ staff: StaffRow[] }>('/api/school/staff/').then((r) => r.data.staff) });
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const [creds, setCreds] = useState<Credentials | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const staff = q.data ?? [];
  const counts = useMemo(() => ({
    all: staff.length,
    teacher: staff.filter((s) => s.role === 'teacher').length,
    counselor: staff.filter((s) => s.role === 'counselor').length,
    school_admin: staff.filter((s) => s.role === 'school_admin').length,
  }), [staff]);
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return staff.filter((s) => (roleFilter === 'all' || s.role === roleFilter)
      && (!needle || `${s.first_name} ${s.last_name} ${s.username}`.toLowerCase().includes(needle)));
  }, [staff, roleFilter, query]);
  const { rows, sort, toggle } = useSortableTable(shown, COLUMNS, { key: 'name', dir: 'asc' });
  const pg = usePagination(rows, PAGE_SIZE, `${roleFilter}|${query}|${sort?.key}${sort?.dir}`);

  const act = useMutation({
    mutationFn: async (p: NonNullable<Pending>) => {
      if (p.kind === 'reset') return (await API.post(`/api/school/staff/${p.s.profile_id}/reset-password/`)).data;
      if (p.kind === 'role') return (await API.patch(`/api/school/staff/${p.s.profile_id}/`, { role: p.s.role === 'teacher' ? 'counselor' : 'teacher' })).data;
      return (await API.patch(`/api/school/staff/${p.s.profile_id}/`, { is_active: p.kind === 'reactivate' })).data;
    },
    onSuccess: (data, p) => {
      qc.invalidateQueries({ queryKey: ['school-staff'] });
      qc.invalidateQueries({ queryKey: ['school-setup-summary'] });
      setPending(null);
      setActionError(null);
      if (p.kind === 'reset') setCreds({ name: fullName(p.s), username: p.s.username, password: data.temporary_password, fresh: false });
    },
    onError: (err) => setActionError(errText(err, 'That did not work. Try again.')),
  });

  const confirmCopy = pending && {
    reset: { title: `Reset ${fullName(pending.s)}'s password?`, body: 'Their current password stops working and they are signed out everywhere. You get a new temporary password to give them.', label: 'Reset password', danger: false },
    deactivate: { title: `Deactivate ${fullName(pending.s)}?`, body: "They can't sign in until you reactivate them. Their classes, marks and notes stay.", label: 'Deactivate', danger: true },
    reactivate: { title: `Reactivate ${fullName(pending.s)}?`, body: 'They can sign in again with their existing password.', label: 'Reactivate', danger: false },
    role: { title: `Make ${fullName(pending.s)} a ${pending.s.role === 'teacher' ? 'counselor' : 'teacher'}?`, body: pending.s.role === 'teacher' ? 'They stop seeing their classes and start seeing wellbeing alerts and cases.' : 'They stop seeing wellbeing cases. Assign their grades and subjects on the Teachers page.', label: 'Change role', danger: false },
  }[pending.kind];

  return (
    <div className="space-y-[18px]">
      <PageHeader eyebrow="School setup" title="Staff" subtitle="Teacher and counselor accounts at your school. Add someone to get their sign-in details."
        actions={<Button icon={<Plus size={16} aria-hidden />} onClick={() => setAdding(true)}>Add staff</Button>} />

      <PanelCard>
        <div className="mb-4 flex flex-col gap-3">
          <label className="relative block max-w-[420px]">
            <span className="sr-only">Search staff</span>
            <Search size={16} aria-hidden className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" />
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name or username" className={`${fieldClass} pl-10`} />
          </label>
          <PillTabs dense label="Role" value={roleFilter} onChange={setRoleFilter} options={[
            { key: 'all', label: 'Everyone', count: counts.all },
            { key: 'teacher', label: 'Teachers', count: counts.teacher },
            { key: 'counselor', label: 'Counselors', count: counts.counselor },
            ...(counts.school_admin ? [{ key: 'school_admin' as const, label: 'School admins', count: counts.school_admin }] : []),
          ]} />
        </div>

        {actionError && <p role="alert" className="mb-3 rounded-[12px] bg-danger-bg px-3 py-2 text-sm text-danger-ink">{actionError}</p>}

        {q.isLoading ? <LoadingState label="Loading staff…" />
          : q.isError ? <ErrorPanel title="Couldn't load staff" body="Try again in a moment." onRetry={() => q.refetch()} />
            : staff.length === 0 ? (
              <EmptyState icon={<UserPlus size={20} />} title="No staff yet"
                body={<>Add your teachers and counselors; each gets a username and a temporary password. <button type="button" className="font-semibold text-primary underline" onClick={() => setAdding(true)}>Add staff</button></>} />
            ) : rows.length === 0 ? (
              <EmptyState icon={<Search size={20} />} title="Nobody matches" body="Try another name or role." />
            ) : (
              <>
                <Table minWidth={760}>
                  <thead>
                    <tr>
                      <SortableTh columnKey="name" sort={sort} onToggle={toggle}>Name</SortableTh>
                      <SortableTh columnKey="role" sort={sort} onToggle={toggle}>Role</SortableTh>
                      <SortableTh columnKey="status" sort={sort} onToggle={toggle}>Status</SortableTh>
                      <SortableTh columnKey="classes" sort={sort} onToggle={toggle} align="right">Classes</SortableTh>
                      <SortableTh columnKey="last" sort={sort} onToggle={toggle}>Last sign-in</SortableTh>
                      <Th align="right"><span className="sr-only">Actions</span></Th>
                    </tr>
                  </thead>
                  <tbody>
                    {pg.pageItems.map((s) => {
                      const admin = s.role === 'school_admin';
                      return (
                        <tr key={s.profile_id} className={s.is_active ? '' : 'opacity-70'}>
                          <Td>
                            <span className="block font-semibold text-ink">{fullName(s)}</span>
                            <span className="block font-mono text-xs text-muted">{s.username}</span>
                          </Td>
                          <Td>{ROLE_LABEL[s.role]}</Td>
                          <Td><StatusBadge s={s} /></Td>
                          <Td align="right">
                            {s.role === 'teacher'
                              ? (s.assignment_count > 0 ? <span className="font-mono">{s.assignment_count}</span>
                                : <Link to="/school-setup/teachers" className="text-[13px] font-semibold text-primary underline">Assign</Link>)
                              : <span className="text-muted">—</span>}
                          </Td>
                          <Td className="whitespace-nowrap text-[13px] text-muted">{lastSeen(s.last_login)}</Td>
                          <Td align="right">
                            {admin ? <span className="text-xs text-muted">Managed by YADA</span> : (
                              <div className="flex flex-wrap justify-end gap-1.5">
                                {s.is_active && <Button size="sm" variant="ghost" onClick={() => setPending({ kind: 'reset', s })}>Reset password</Button>}
                                {s.is_active && <Button size="sm" variant="ghost" onClick={() => setPending({ kind: 'role', s })}>Make {s.role === 'teacher' ? 'counselor' : 'teacher'}</Button>}
                                <Button size="sm" variant={s.is_active ? 'danger-outline' : 'secondary'} onClick={() => setPending({ kind: s.is_active ? 'deactivate' : 'reactivate', s })}>
                                  {s.is_active ? 'Deactivate' : 'Reactivate'}
                                </Button>
                              </div>
                            )}
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
                <div className="mt-3.5 flex flex-wrap items-center justify-between gap-2.5 border-t border-hairline pt-3.5">
                  <span className="text-[13px] text-muted">{rows.length} {rows.length === 1 ? 'person' : 'people'}</span>
                  <Pager page={pg.page} pageCount={pg.pageCount} onPage={pg.setPage} label="Staff pagination" />
                </div>
              </>
            )}
      </PanelCard>

      <AddStaffDrawer open={adding} onClose={() => setAdding(false)} onCreated={(c) => { setAdding(false); setCreds(c); }} />
      <CredentialsDialog creds={creds} onClose={() => setCreds(null)} />
      <ConfirmDialog isOpen={pending !== null} title={confirmCopy?.title ?? ''} body={confirmCopy?.body ?? ''}
        confirmLabel={confirmCopy?.label} danger={confirmCopy?.danger} busy={act.isPending}
        onConfirm={() => pending && act.mutate(pending)} onCancel={() => { setPending(null); setActionError(null); }} />
    </div>
  );
}
